import { Amplify } from "aws-amplify";
import { fetchAuthSession, signInWithRedirect, signOut as amplifySignOut } from "aws-amplify/auth";

export interface AuthUser {
  /**
   * The Cognito `sub`, which is the id every service records as the actor.
   *
   * Carried so a surface can tell "you did this" from "someone else did", the
   * only question a bare actor id can answer without a name-resolution path.
   * Empty when the token carries no sub, which is not an identity and must not
   * be compared as one.
   */
  sub: string;
  email: string;
  name: string;
  picture: string;
}

export function configureAmplify(): void {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const userPoolClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  const rawCognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;

  if (!userPoolId || !userPoolClientId || !rawCognitoDomain) {
    throw new Error(
      "Missing Cognito configuration. Set VITE_COGNITO_USER_POOL_ID, VITE_COGNITO_CLIENT_ID, and VITE_COGNITO_DOMAIN."
    );
  }

  // Amplify v6 prepends "https://" when building OAuth URLs, so the domain
  // must be a bare hostname (e.g. "cognito.staging.letsendure.dev").
  // The Pulumi stack exports the value with "https://"; strip it here.
  const domain = rawCognitoDomain.replace(/^https?:\/\//, "").replace(/\/+$/, "");

  const currentOrigin = window.location.origin;

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        loginWith: {
          oauth: {
            domain,
            scopes: ["openid", "email", "profile"],
            redirectSignIn: [`${currentOrigin}/`],
            redirectSignOut: [`${currentOrigin}/`],
            responseType: "code",
          },
        },
      },
    },
  });
}

export async function signIn(): Promise<void> {
  await signInWithRedirect({ provider: "Google" });
}

export async function signOut(): Promise<void> {
  await amplifySignOut();
}

export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    if (!idToken) return null;
    const claims = idToken.payload;
    return {
      sub: (claims["sub"] as string) ?? "",
      email: (claims["email"] as string) ?? "",
      name: (claims["name"] as string) ?? "",
      picture: (claims["picture"] as string) ?? "",
    };
  } catch {
    return null;
  }
}
